export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  return function (this: any, ...args: Parameters<T>) {
    const context = this
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(context, args), wait)
  }
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  let previous = 0

  return function (this: any, ...args: Parameters<T>) {
    const context = this
    const now = Date.now()
    const remaining = wait - (now - previous)

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      previous = now
      func.apply(context, args)
    } else if (!timeout) {
      timeout = setTimeout(() => {
        previous = Date.now()
        timeout = null
        func.apply(context, args)
      }, remaining)
    }
  }
}

export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') return obj
  if (obj instanceof Date) return new Date(obj.getTime()) as any
  if (obj instanceof Array) return obj.map((item) => deepClone(item)) as any
  if (obj instanceof Object) {
    const clonedObj: any = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key])
      }
    }
    return clonedObj
  }
  return obj
}

export function deepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true
  if (typeof obj1 !== typeof obj2) return false
  if (typeof obj1 !== 'object' || obj1 === null || obj2 === null) return obj1 === obj2
  if (Array.isArray(obj1) !== Array.isArray(obj2)) return false
  if (Array.isArray(obj1)) {
    if (obj1.length !== obj2.length) return false
    return obj1.every((item, index) => deepEqual(item, obj2[index]))
  }
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)
  if (keys1.length !== keys2.length) return false
  return keys1.every((key) => deepEqual(obj1[key], obj2[key]))
}

export function uniqueId(prefix: string = ''): string {
  return prefix + Math.random().toString(36).substring(2, 9) + Date.now().toString(36)
}

export function uuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function random(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export function randomString(length: number, chars?: string): string {
  const defaultChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  const charSet = chars || defaultChars
  let result = ''
  for (let i = 0; i < length; i++) {
    result += charSet[Math.floor(Math.random() * charSet.length)]
  }
  return result
}

export function shuffle<T>(array: T[]): T[] {
  const result = [...array]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

export function chunk<T>(array: T[], size: number): T[][] {
  const result: T[][] = []
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size))
  }
  return result
}

export function flatten<T>(array: any[]): T[] {
  return array.reduce((acc, val) => acc.concat(Array.isArray(val) ? flatten(val) : val), [])
}

export function groupBy<T>(array: T[], key: keyof T | ((item: T) => any)): Record<string, T[]> {
  return array.reduce((result: Record<string, T[]>, item) => {
    const groupKey = typeof key === 'function' ? key(item) : item[key]
    const group = String(groupKey)
    if (!result[group]) {
      result[group] = []
    }
    result[group].push(item)
    return result
  }, {})
}

export function sortBy<T>(array: T[], key: keyof T | ((item: T) => any), order: 'asc' | 'desc' = 'asc'): T[] {
  return [...array].sort((a, b) => {
    const valueA = typeof key === 'function' ? key(a) : a[key]
    const valueB = typeof key === 'function' ? key(b) : b[key]
    if (valueA < valueB) return order === 'asc' ? -1 : 1
    if (valueA > valueB) return order === 'asc' ? 1 : -1
    return 0
  })
}

export function pick<T extends Record<string, any>, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
  return keys.reduce((result, key) => {
    if (obj.hasOwnProperty(key)) {
      result[key] = obj[key]
    }
    return result
  }, {} as Pick<T, K>)
}

export function omit<T extends Record<string, any>, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> {
  return Object.keys(obj)
    .filter((key) => !keys.includes(key as K))
    .reduce((result, key) => {
      result[key as Exclude<keyof T, K>] = obj[key as Exclude<keyof T, K>]
      return result
    }, {} as Omit<T, K>)
}

export function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text)
}

export function getQueryParam(key: string, url?: string): string | null {
  const searchUrl = url || window.location.search
  const params = new URLSearchParams(searchUrl)
  return params.get(key)
}

export function setQueryParam(key: string, value: string, url?: string): string {
  const currentUrl = url || window.location.href
  const [base, search] = currentUrl.split('?')
  const params = new URLSearchParams(search || '')
  params.set(key, value)
  return `${base}?${params.toString()}`
}

export function removeQueryParam(key: string, url?: string): string {
  const currentUrl = url || window.location.href
  const [base, search] = currentUrl.split('?')
  const params = new URLSearchParams(search || '')
  params.delete(key)
  const queryString = params.toString()
  return queryString ? `${base}?${queryString}` : base
}

export function parseQueryString(query: string): Record<string, string> {
  const params = new URLSearchParams(query)
  const result: Record<string, string> = {}
  params.forEach((value, key) => {
    result[key] = value
  })
  return result
}

export function stringifyQueryString(obj: Record<string, any>): string {
  const params = new URLSearchParams()
  Object.entries(obj).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      params.set(key, String(value))
    }
  })
  return params.toString()
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function retry<T>(fn: () => Promise<T>, maxRetries: number = 3, delay: number = 1000): Promise<T> {
  return new Promise((resolve, reject) => {
    let retries = 0
    const attempt = () => {
      fn()
        .then(resolve)
        .catch((error) => {
          retries++
          if (retries < maxRetries) {
            setTimeout(attempt, delay)
          } else {
            reject(error)
          }
        })
    }
    attempt()
  })
}

export function once<T extends (...args: any[]) => any>(fn: T): T {
  let called = false
  let result: any
  return function (this: any, ...args: Parameters<T>) {
    if (!called) {
      called = true
      result = fn.apply(this, args)
    }
    return result
  } as T
}

export function memoize<T extends (...args: any[]) => any>(fn: T): T {
  const cache = new Map<string, any>()
  return function (this: any, ...args: Parameters<T>) {
    const key = JSON.stringify(args)
    if (cache.has(key)) {
      return cache.get(key)
    }
    const result = fn.apply(this, args)
    cache.set(key, result)
    return result
  } as T
}
